import { useConversationLifecycle } from '../features/conversations/hooks/use-conversation-lifecycle';
import { useToastStore } from '../stores/toast-store';

import { CapabilityGate } from './CapabilityGate';

export interface ConversationReopenModalProps {
  conversationId: string;
  open: boolean;
  onClose: () => void;
}

/**
 * ConversationReopenModal — reabre uma conversa em estado terminal (Briefing 2A §2.8).
 *
 * Gated pela capability `reopen-closed-conversations` (mapeada a Supervisor+/
 * Gestor/Admin tenant/Admin global via role-mapping). Atendente NÃO tem.
 *
 * `useConversationLifecycle.reopen()` chama `POST /v1/conversations/:id/reopen`;
 * o backend transita o estado, audita e devolve `{ status, mode: 'reopen-same' }`.
 */
export function ConversationReopenModal({
  conversationId,
  open,
  onClose,
}: ConversationReopenModalProps): JSX.Element | null {
  const lifecycle = useConversationLifecycle(conversationId);

  if (!open) return null;

  const handleConfirm = (): void => {
    void lifecycle
      .reopen()
      .then(() => {
        onClose();
        useToastStore.getState().push({
          variant: 'success',
          title: 'Conversa reaberta',
          description: 'A conversa voltou para o estado anterior.',
          durationMs: 3500,
        });
      })
      .catch((err: unknown) => {
        useToastStore.getState().push({
          variant: 'error',
          title: 'Falha ao reabrir',
          description: err instanceof Error ? err.message : 'Erro desconhecido',
          durationMs: 6000,
        });
      });
  };

  return (
    <CapabilityGate capability="reopen-closed-conversations">
      <div
        role="dialog"
        aria-label="Reabrir conversa"
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      >
        <div
          className="w-full max-w-md rounded-lg p-6 shadow-xl"
          style={{ background: 'var(--bg-surface)' }}
        >
          <h2 className="mb-3 text-lg font-semibold">Reabrir conversa</h2>
          <p className="text-sm" style={{ color: 'var(--fg-2)' }}>
            A conversa voltará para o estado anterior. Esta ação será registrada na auditoria.
          </p>

          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              className="rounded border px-4 py-2 text-sm"
              style={{ borderColor: 'var(--border-default)' }}
              onClick={onClose}
              disabled={lifecycle.isReopening}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={handleConfirm}
              disabled={lifecycle.isReopening}
            >
              {lifecycle.isReopening ? 'Reabrindo…' : 'Reabrir'}
            </button>
          </div>
        </div>
      </div>
    </CapabilityGate>
  );
}
